import { Save, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";

import { ConfirmDialog } from "./ConfirmDialog.tsx";

import { Alert } from "@bigpp/ui/components/alert";
import { Button } from "@bigpp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bigpp/ui/components/dialog";
import { Input } from "@bigpp/ui/components/input";
import { Label } from "@bigpp/ui/components/label";
import { Textarea } from "@bigpp/ui/components/textarea";

import { trpc } from "../trpc.ts";

// Slug is identity for skills (the agent references it by name) so we derive
// once from the human name and keep it stable thereafter. Hidden from the UI
// per the request.
const slugify = (s: string): string =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** undefined → create mode. uuid → edit mode. */
  skillId: string | undefined;
};

export function SkillEditorDialog({ open, onOpenChange, skillId }: Props) {
  const isEdit = skillId !== undefined;
  const utils = trpc.useUtils();

  const existing = trpc.skills.get.useQuery(
    { id: skillId ?? "" },
    { enabled: isEdit && open },
  );

  // Used to surface client-side conflict feedback before submitting.
  const list = trpc.skills.list.useQuery(undefined, { enabled: open });

  const create = trpc.skills.create.useMutation({
    onSuccess: () => {
      void utils.skills.list.invalidate();
      void utils.runs.listSkills.invalidate();
      onOpenChange(false);
    },
  });
  const update = trpc.skills.update.useMutation({
    onSuccess: () => {
      void utils.skills.list.invalidate();
      void utils.runs.listSkills.invalidate();
      onOpenChange(false);
    },
  });
  const del = trpc.skills.delete.useMutation({
    onSuccess: () => {
      void utils.skills.list.invalidate();
      void utils.runs.listSkills.invalidate();
      onOpenChange(false);
    },
  });

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [body, setBody] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Slug is locked to the existing value in edit mode (so agent references
  // keep working) and derived from the name on create.
  const [lockedSlug, setLockedSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      if (existing.data) {
        setName(existing.data.name);
        setDescription(existing.data.description);
        setBody(existing.data.body);
        setLockedSlug(existing.data.slug);
      }
    } else {
      setName("");
      setDescription("");
      setBody("");
      setLockedSlug(null);
    }
  }, [open, isEdit, existing.data]);

  const derivedSlug = useMemo(() => slugify(name), [name]);
  const effectiveSlug = lockedSlug ?? derivedSlug;

  // Client-side conflict check: only matters in create mode (slug is locked on
  // edit). System skills can share a slug — uniqueness is scoped to user kind.
  const slugConflict = useMemo(() => {
    if (isEdit || !derivedSlug || !list.data) return false;
    return list.data.some((s) => s.kind === "user" && s.slug === derivedSlug);
  }, [isEdit, derivedSlug, list.data]);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!effectiveSlug) return;
    if (slugConflict) return;
    if (isEdit && skillId) {
      // Don't send slug — backend keeps existing.
      update.mutate({ id: skillId, name, description, body });
    } else {
      create.mutate({ slug: derivedSlug, name, description, body });
    }
  };

  const pending = create.isPending || update.isPending || del.isPending;
  const error = create.error ?? update.error ?? del.error ?? null;
  const loadingExisting = isEdit && existing.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit skill" : "New skill"}</DialogTitle>
          <DialogDescription>
            Short reusable recipes PowerSlide can load on demand, or that you can
            force-activate for every turn.
          </DialogDescription>
        </DialogHeader>

        {loadingExisting ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">Loading…</p>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="skill-name">Name</Label>
              <Input
                id="skill-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                placeholder="House style"
                required
                maxLength={120}
              />
              {!isEdit && slugConflict && (
                <p className="text-[11px] text-[var(--color-destructive)]">
                  You already have a skill with this name. Pick a different one.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="skill-description">Description</Label>
              <Input
                id="skill-description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                placeholder="One line. Shown to PowerSlide in the skill index."
                maxLength={240}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="skill-body">Body</Label>
              <Textarea
                id="skill-body"
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                }}
                placeholder={"# House style\n\n- Title font: Inter Tight\n- Accent color: #f97316\n…"}
                required
                rows={14}
                className="font-mono text-xs min-h-[260px]"
              />
              <p className="text-[10px] text-[var(--color-muted-foreground)]">
                {body.length.toString()} chars
              </p>
            </div>

            {error && <Alert variant="destructive">{error.message}</Alert>}

            <DialogFooter>
              {isEdit && skillId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={pending}
                  onClick={() => {
                    setConfirmDelete(true);
                  }}
                  className="mr-auto text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
                >
                  <Trash2 />
                  Delete
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                }}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={pending || slugConflict || !effectiveSlug}
              >
                <Save />
                {pending ? "Saving…" : isEdit ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
      {isEdit && skillId && (
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title="Delete this skill?"
          description={`"${name}" will be removed permanently. This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          pending={del.isPending}
          onConfirm={() => {
            del.mutate({ id: skillId });
            setConfirmDelete(false);
          }}
        />
      )}
    </Dialog>
  );
}
